import { Injectable, inject, signal } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments';
import { AuthTokenService } from 'src/app/core/services/auth-token.service';
import { SUPPORT_API, SUPPORT_SOCKET_EVENTS } from '../support-system.config';
import {
  SupportApiChat,
  SupportApiEnvelope,
  SupportApiMessage,
  SupportConnectionState,
} from '../support-system.models';
import {
  SupportApiError,
  toSupportError,
  unwrapEnvelope,
} from './support-api.service';

const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 60_000;

/**
 * Live updates from `/api/v1/support/socket.io`. Notification only: REST
 * stays the source of truth, so after every reconnect the chat service
 * re-joins its chats and fetches what it missed.
 */
@Injectable({ providedIn: 'root' })
export class SupportSocketService {
  private authToken = inject(AuthTokenService);

  readonly state = signal<SupportConnectionState>('idle');

  private readonly connectedSubject = new Subject<{ reconnect: boolean }>();
  /** Every successful connect; `reconnect` is false only for the first one. */
  readonly connected$ = this.connectedSubject.asObservable();
  private readonly messageSubject = new Subject<SupportApiMessage>();
  readonly message$ = this.messageSubject.asObservable();
  private readonly chatSubject = new Subject<SupportApiChat>();
  /**
   * Chat summaries from `chat:assigned` and `chat:closed`, plus
   * `chat:created` and `chat:updated` once subscribed to the inbox.
   */
  readonly chat$ = this.chatSubject.asObservable();

  private socket: Socket | null = null;
  private subscriptions = new Subscription();
  private everConnected = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = RETRY_MIN_MS;

  /** Idempotent. */
  connect(): void {
    if (this.socket) return;

    // Always the EU API, US accounts included: support runs on one backend.
    const address = new URL(environment.address);
    const socket = new Socket({
      url: address.origin,
      options: {
        path: address.pathname.replace(/\/$/, '') + SUPPORT_API.socketPath,
        // The backend accepts WebSocket only (no sticky sessions for polling).
        transports: ['websocket'],
        autoConnect: false,
        // A function, so every reconnect sends the current token.
        auth: (cb: (data: object) => void) =>
          cb({ token: this.authToken.getToken() || '' }),
      } as any,
    });
    this.socket = socket;
    this.everConnected = false;
    this.subscriptions = new Subscription();

    this.on('connect', () => {
      this.state.set('connected');
      this.retryDelay = RETRY_MIN_MS;
      this.connectedSubject.next({ reconnect: this.everConnected });
      this.everConnected = true;
    });
    this.on('disconnect', (reason: string) => {
      this.state.set('disconnected');
      // socket.io retries by itself, except when the server dropped us
      // (expired token, lost access).
      if (reason === 'io server disconnect') this.scheduleRetry();
    });
    this.on('connect_error', () => {
      this.state.set('disconnected');
      // Rejected by the server's auth check: socket.io won't retry.
      if (!socket.ioSocket.active) this.scheduleRetry();
    });
    this.on(SUPPORT_SOCKET_EVENTS.message, (message: SupportApiMessage) =>
      this.messageSubject.next(message),
    );
    this.on(SUPPORT_SOCKET_EVENTS.assigned, (chat: SupportApiChat) =>
      this.chatSubject.next(chat),
    );
    this.on(SUPPORT_SOCKET_EVENTS.closed, (chat: SupportApiChat) =>
      this.chatSubject.next(chat),
    );
    this.on(SUPPORT_SOCKET_EVENTS.created, (chat: SupportApiChat) =>
      this.chatSubject.next(chat),
    );
    this.on(SUPPORT_SOCKET_EVENTS.updated, (chat: SupportApiChat) =>
      this.chatSubject.next(chat),
    );

    this.state.set('connecting');
    socket.connect();
  }

  disconnect(): void {
    this.clearRetry();
    this.subscriptions.unsubscribe();
    this.socket?.disconnect();
    this.socket = null;
    this.state.set('idle');
  }

  /** Subscribes to the chat's live messages; answers with its summary. */
  join(chatId: string): Observable<SupportApiChat> {
    return this.request(SUPPORT_SOCKET_EVENTS.join, { chatId });
  }

  leave(chatId: string): Observable<null> {
    return this.request(SUPPORT_SOCKET_EVENTS.leave, { chatId });
  }

  /** Team only: summaries of every chat. Lost on reconnect, like rooms. */
  subscribeInbox(): Observable<null> {
    return this.request(SUPPORT_SOCKET_EVENTS.subscribeInbox, {});
  }

  private request<T>(event: string, payload: object): Observable<T> {
    return new Observable<T>((observer) => {
      const socket = this.socket;
      if (!socket?.ioSocket.connected) {
        observer.error(new SupportApiError('OFFLINE', 'Socket not connected'));
        return;
      }
      socket.ioSocket
        .timeout(SUPPORT_API.timeoutMs)
        .emit(event, payload, (err: unknown, reply: SupportApiEnvelope<T>) => {
          try {
            if (err) throw new SupportApiError('TIMEOUT', 'No ack from the server');
            observer.next(unwrapEnvelope(reply));
            observer.complete();
          } catch (error) {
            observer.error(toSupportError(error));
          }
        });
    });
  }

  private on<T>(event: string, handler: (payload: T) => void): void {
    this.subscriptions.add(this.socket!.fromEvent<T>(event).subscribe(handler));
  }

  private scheduleRetry(): void {
    if (this.retryTimer || !this.authToken.getToken()) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.socket && !this.socket.ioSocket.connected) {
        this.state.set('connecting');
        this.socket.connect();
      }
    }, this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryDelay = RETRY_MIN_MS;
  }
}
